import { useCallback } from 'react'
import { Content, TDocumentDefinitions } from 'pdfmake/interfaces'
import { COMMENTS_ON_INSTRUCTIONS, DEFAULT_PDF_DOC_TXT } from './constants'
import {
  TCommonsCognusARDocData,
  TCommunityCognusARDocData,
  TVendorCognusARDocData
} from './types'
import { useDocContentGeneration } from './useDocContentGeneration'

export const useFinancialsCognusARDocGeneration = () => {
  const {
    generateDocumentInfo,
    generateFromToSection,
    generateCommentsAndTotalSection,
    launchDocGeneration,
    getPdfFileName,
    generateCognusARInvoiceTable
  } = useDocContentGeneration()

  const _generateCommunityInvoiceDocHeader = useCallback(
    (communityData: TCommunityCognusARDocData['communityData']): Content[] => [
      ...generateDocumentInfo({
        docTitle: DEFAULT_PDF_DOC_TXT.INVOICE,
        docHeaderName: DEFAULT_PDF_DOC_TXT.INVOICE,
        docDate: communityData.invoice_date,
        docId: communityData.document_number,
        docDueOn: communityData.due_date
      }),
      ...generateFromToSection(
        {
          name: communityData.cognus_name,
          address: communityData.cognus_address,
          id: communityData.health_system_id
        },
        {
          name: communityData.health_system_name,
          address: communityData.health_system_address,
          phoneNumber: communityData.health_system_phone_number
        }
      )
    ],
    [generateDocumentInfo, generateFromToSection]
  )

  const _generateCommonsRemitAdviceDocHeader = useCallback(
    (commonsData: TCommonsCognusARDocData['commonsData']): Content[] => [
      ...generateDocumentInfo({
        docTitle: DEFAULT_PDF_DOC_TXT.REMITTANCE_ADVICE,
        docHeaderName: DEFAULT_PDF_DOC_TXT.REMIT_ADVICE,
        docDate: commonsData.invoice_date,
        docId: commonsData.document_number,
        docDueOn: commonsData.due_date
      }),
      ...generateFromToSection(
        {
          name: commonsData.cognus_name,
          address: commonsData.cognus_address,
          id: commonsData.health_system_id
        },
        {
          name: commonsData.supplier_name,
          address: commonsData.supplier_address,
          phoneNumber: commonsData.supplier_phone_number
        }
      )
    ],
    [generateDocumentInfo, generateFromToSection]
  )

  const _generateVendorInvoiceDocHeader = useCallback(
    (vendorData: TVendorCognusARDocData['vendorData']): Content[] => [
      ...generateDocumentInfo({
        docTitle: DEFAULT_PDF_DOC_TXT.INVOICE,
        docHeaderName: DEFAULT_PDF_DOC_TXT.INVOICE,
        docDate: vendorData.invoice_date,
        docId: vendorData.document_number,
        docDueOn: vendorData.due_date
      }),
      ...generateFromToSection(
        {
          name: vendorData.cognus_name,
          address: vendorData.cognus_address,
          id: vendorData.health_system_id
        },
        {
          name: vendorData.supplier_name,
          address: vendorData.supplier_address,
          phoneNumber: vendorData.supplier_phone_number
        }
      )
    ],
    [generateDocumentInfo, generateFromToSection]
  )

  const generateCommunityCognusARInvoiceDoc = useCallback(
    ({ communityData, callback }: TCommunityCognusARDocData) => {
      const content: TDocumentDefinitions['content'] = [
        ..._generateCommunityInvoiceDocHeader(communityData),
        ...generateCognusARInvoiceTable(communityData.invoices),
        ...generateCommentsAndTotalSection({
          comments: [COMMENTS_ON_INSTRUCTIONS.CREDIT],
          data: [
            {
              label: DEFAULT_PDF_DOC_TXT.TOTAL_DUE,
              value: communityData.total
            }
          ]
        })
      ]
      launchDocGeneration(
        content,
        getPdfFileName(
          DEFAULT_PDF_DOC_TXT.INVOICE_DOC_NAME,
          communityData.document_number
        ),
        callback
      )
    },
    [
      _generateCommunityInvoiceDocHeader,
      launchDocGeneration,
      generateCognusARInvoiceTable,
      generateCommentsAndTotalSection,
      getPdfFileName
    ]
  )

  const generateCommonsCognusARRemitAdviceDoc = useCallback(
    ({ commonsData, callback }: TCommonsCognusARDocData) => {
      const content: TDocumentDefinitions['content'] = [
        ..._generateCommonsRemitAdviceDocHeader(commonsData),
        ...generateCognusARInvoiceTable(commonsData.invoices),
        ...generateCommentsAndTotalSection({
          comments: [COMMENTS_ON_INSTRUCTIONS.CREDIT],
          data: [
            {
              label: DEFAULT_PDF_DOC_TXT.TOTAL,
              value: commonsData.total
            }
          ]
        })
      ]
      launchDocGeneration(
        content,
        getPdfFileName(
          DEFAULT_PDF_DOC_TXT.REMIT_ADVICE,
          commonsData.document_number
        ),
        callback
      )
    },
    [
      _generateCommonsRemitAdviceDocHeader,
      launchDocGeneration,
      generateCognusARInvoiceTable,
      generateCommentsAndTotalSection,
      getPdfFileName
    ]
  )

  const generateVendorCognusARInvoiceDoc = useCallback(
    ({ vendorData, callback }: TVendorCognusARDocData) => {
      const content: TDocumentDefinitions['content'] = [
        ..._generateVendorInvoiceDocHeader(vendorData),
        ...generateCognusARInvoiceTable(vendorData.invoices),
        ...generateCommentsAndTotalSection({
          comments: [COMMENTS_ON_INSTRUCTIONS.DEBIT],
          data: [
            {
              label: DEFAULT_PDF_DOC_TXT.TOTAL_DUE,
              value: vendorData.total
            }
          ]
        })
      ]
      launchDocGeneration(
        content,
        getPdfFileName(
          DEFAULT_PDF_DOC_TXT.INVOICE_DOC_NAME,
          vendorData.document_number
        ),
        callback
      )
    },
    [
      _generateVendorInvoiceDocHeader,
      launchDocGeneration,
      generateCognusARInvoiceTable,
      generateCommentsAndTotalSection,
      getPdfFileName
    ]
  )

  return {
    generateCommunityCognusARInvoiceDoc,
    generateCommonsCognusARRemitAdviceDoc,
    generateVendorCognusARInvoiceDoc
  }
}
